import { createAsyncThunk } from '@reduxjs/toolkit';

import callApi from '../../../service/callApi';
import sendRequest from '../../../service/sendRequest';
import { loadFontFace, removeFontFamily } from '../../../utils/fontFace';

export const GET_MEDIA_STUDIO_IMAGES = 'mediaStudioSettings/GET_MEDIA_STUDIO_IMAGES';
export const UPLOAD_MEDIA_STUDIO_IMAGE = 'mediaStudioSettings/UPLOAD_MEDIA_STUDIO_IMAGE';
export const DELETE_MEDIA_STUDIO_IMAGE = 'mediaStudioSettings/DELETE_MEDIA_STUDIO_IMAGE';
export const GET_MEDIA_STUDIO_FONTS = 'mediaStudioSettings/GET_MEDIA_STUDIO_FONTS';
export const UPLOAD_MEDIA_STUDIO_FONT = 'mediaStudioSettings/UPLOAD_MEDIA_STUDIO_FONT';
export const DELETE_MEDIA_STUDIO_FONT = 'mediaStudioSettings/DELETE_MEDIA_STUDIO_FONT';
export const LOAD_MEDIA_STUDIO_FONT_FILE = 'mediaStudioSettings/LOAD_MEDIA_STUDIO_FONT_FILE';
export const GET_MEDIA_STUDIO_COLORS = 'mediaStudioSettings/GET_MEDIA_STUDIO_COLORS';
export const ADD_MEDIA_STUDIO_COLOR = 'mediaStudioSettings/ADD_MEDIA_STUDIO_COLOR';
export const DELETE_MEDIA_STUDIO_COLOR = 'mediaStudioSettings/DELETE_MEDIA_STUDIO_COLOR';

const getFontFamily = (id) => `ms-settings-font-${id}`;

let pendingSettingsChange = Promise.resolve();

const queueSettingsChange = (change) => {
  const result = pendingSettingsChange.then(change);
  pendingSettingsChange = result.catch(() => null);
  return result;
};

export const getMediaStudioImages = createAsyncThunk(GET_MEDIA_STUDIO_IMAGES, async () => {
  const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/images`));

  return response.data ?? [];
});

export const uploadMediaStudioImage = createAsyncThunk(UPLOAD_MEDIA_STUDIO_IMAGE, (file) =>
  queueSettingsChange(async () => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('label', file.name);

    await sendRequest(() =>
      callApi.request({
        url: `/api/media/manual-editor/image`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        method: 'POST',
        data: formData,
      }),
    );

    const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/images`));

    return response.data ?? [];
  }),
);

export const deleteMediaStudioImage = createAsyncThunk(DELETE_MEDIA_STUDIO_IMAGE, async (id) => {
  await queueSettingsChange(() => sendRequest(() => callApi.delete(`/api/media/manual-editor/image?id=${id}`)));

  return id;
});

export const getMediaStudioFonts = createAsyncThunk(GET_MEDIA_STUDIO_FONTS, async () => {
  const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/fonts`));

  return response.data ?? [];
});

export const uploadMediaStudioFont = createAsyncThunk(UPLOAD_MEDIA_STUDIO_FONT, ({ file, label }) =>
  queueSettingsChange(async () => {
    const formData = new FormData();
    formData.append('font', file, file.name);
    formData.append('label', label);

    await sendRequest(() =>
      callApi.request({
        url: `/api/media/manual-editor/font`,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        method: 'POST',
        data: formData,
      }),
    );

    const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/fonts`));

    return response.data ?? [];
  }),
);

export const deleteMediaStudioFont = createAsyncThunk(DELETE_MEDIA_STUDIO_FONT, async (id) => {
  await queueSettingsChange(() => sendRequest(() => callApi.delete(`/api/media/manual-editor/font?id=${id}`)));

  removeFontFamily(getFontFamily(id));

  return id;
});

export const loadMediaStudioFontFile = createAsyncThunk(
  LOAD_MEDIA_STUDIO_FONT_FILE,
  async (id) => {
    const response = await sendRequest(
      () => callApi.get(`/api/media/manual-editor/font?sessionId=${id}`, { responseType: 'arraybuffer' }),
      { onError: () => null },
    );

    const family = getFontFamily(id);
    await loadFontFace(family, response.data);

    return { id, family };
  },
  {
    condition: (id, { getState }) => {
      const { fontFamilies, loadingFontFileIds, failedFontFileIds } = getState().mediaStudioSettings;

      return !fontFamilies[id] && !loadingFontFileIds.includes(id) && !failedFontFileIds.includes(id);
    },
  },
);

export const getMediaStudioColors = createAsyncThunk(GET_MEDIA_STUDIO_COLORS, async () => {
  const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/colors`));

  return response.data ?? [];
});

export const addMediaStudioColor = createAsyncThunk(ADD_MEDIA_STUDIO_COLOR, ({ label, color }) =>
  queueSettingsChange(async () => {
    await sendRequest(() =>
      callApi.post(`/api/media/manual-editor/color`, null, { params: { color: color.toUpperCase(), label } }),
    );

    const response = await sendRequest(() => callApi.get(`/api/media/manual-editor/colors`));

    return response.data ?? [];
  }),
);

export const deleteMediaStudioColor = createAsyncThunk(DELETE_MEDIA_STUDIO_COLOR, async (id) => {
  await queueSettingsChange(() => sendRequest(() => callApi.delete(`/api/media/manual-editor/color?id=${id}`)));

  return id;
});
